import { TOTAL_PROGRAM_TYPE } from 'spw-constants';
import {
  Growth,
  Scenario,
  ICategoryNote,
  ITotalProgram,
  GrowthEntry,
  CalculatorType,
  UsfType,
} from 'spw-redux/modules/calculators/types';
import { IDepartment, IBuilding } from 'spw-redux/modules/projects/types';
import { max } from 'lodash';
import { head } from 'ramda';
import ProgramScenarioFactory from './ProgramScenarioFactory';
import ProgramScenario, {
  IProgramScenario,
  PlainProgramScenario,
} from './Scenario';
import { calculateGrowthEntries } from './utils';

type TPSScenario =
  | ProgramScenario
  | PlainProgramScenario
  | TotalProgramScenario;

const isPlainProgramScenariosList = (
  list: any
): list is PlainProgramScenario[] =>
  list && list.length && list[0].headcountUtilizationPercentage !== undefined;

export default class TotalProgramScenario implements IProgramScenario {
  headcountWithUtilization: number;

  headcount: number;

  headcountWithVacancy: number;

  headcountUtilizationPercentage: number;

  workSeats: number;

  workSeatsOriginal: number;

  vacancyFactorPercentage: number;

  name: string;

  deskSharingRatio: number;

  note: string;

  id: number;

  index: number;

  subtotalMultiplierForCirculation: number;

  planningFactorPercentage: number;

  rsfLoadFactorPercentage: number;

  isRsfLoadFactorSetByUser: boolean;

  totalAvailableUSF?: number;

  totalAvailableRSF?: number;

  isDetailed: boolean;

  isShared: boolean;

  primaryLabel: string;

  secondaryLabel?: string;

  compoundLabel?: string;

  targetYear: number;

  startYear: number;

  calculatorType: CalculatorType;

  projectId: number;

  endYear: number;

  targetUSF?: number;

  department?: IDepartment;

  departmentId: number;

  growths: Growth[];

  headcountSeatCountGrowthRate?: number;

  headcountSeatCountBase?: number;

  growthYears?: number;

  storedWorkSeats: number;

  areStoredWorkSeatsOutdated: boolean;

  floorId?: number;

  base?: number;

  calculationsLabel: string;

  calculationsTitlePrefix: string;

  growth: Growth;

  building?: IBuilding;

  usfType?: UsfType;

  linkedScenarios: PlainProgramScenario[];

  growthPercentage = 0;

  notes: ICategoryNote[] = [];

  private shouldCalculateAvailableUSF = true;

  constructor(
    apiScenario: ITotalProgram,
    linkedScenarios: Scenario[] | PlainProgramScenario[]
  ) {
    this.id = apiScenario.id;
    this.name = apiScenario.name;
    this.usfType = apiScenario.usfType;
    this.projectId = apiScenario.projectId;
    this.calculatorType = TOTAL_PROGRAM_TYPE;
    this.shouldCalculateAvailableUSF =
      this.usfType === 'sum_linked_scenarios' || !this.usfType;

    if (!this.shouldCalculateAvailableUSF) {
      this.totalAvailableUSF = apiScenario.usf;
    }

    if (isPlainProgramScenariosList(linkedScenarios)) {
      this.linkedScenarios = linkedScenarios || [];
    } else {
      this.linkedScenarios =
        linkedScenarios?.map((ls) =>
          ProgramScenarioFactory.create(ls).toPlainObject()
        ) || [];
    }

    this.useFirstLinkedScenarioConfig();
    this.growthYears = max(this.linkedScenarios.map((s) => s.growthYears));
    this.endYear = this.startYear + this.growthYears;
    const growthEntries = calculateGrowthEntries({
      startYear: this.startYear,
      growthYears: this.growthYears,
      headcountSeatCountBase: 0,
      headcountSeatCountGrowthRate: 0,
    });

    this.growths = [
      {
        growthEntries,
        headcountSeatCountBase: 0,
        headcountSeatCountGrowthRate: 0,
        aggregation: true,
      },
    ];

    this.calculateTotals();
    this.primaryLabel = 'Multi Departments';
    const targetYearString =
      this.targetYear === this.startYear
        ? `Current (${this.targetYear})`
        : `Year ${this.targetYear - this.startYear} (${this.targetYear})`;
    this.compoundLabel = targetYearString;
    this.calculationsTitlePrefix = 'Total';
    this.calculationsLabel = '';
    this.notes = apiScenario.notes;
  }

  useFirstLinkedScenarioConfig = () => {
    if (this.linkedScenarios.length) {
      const firstScenario = this.linkedScenarios[0];
      this.isDetailed = firstScenario.isDetailed;
      this.isShared = firstScenario.isShared;
      this.startYear = firstScenario.startYear;
      this.targetYear = firstScenario.targetYear;
      this.rsfLoadFactorPercentage = firstScenario.rsfLoadFactorPercentage;
      this.isRsfLoadFactorSetByUser = firstScenario.isRsfLoadFactorSetByUser;
    }
  };

  calculateTotals = () => {
    if (this.shouldCalculateAvailableUSF) this.totalAvailableUSF = 0;

    this.headcount = 0;
    this.headcountWithUtilization = 0;
    this.headcountWithVacancy = 0;
    this.workSeats = 0;

    this.linkedScenarios.forEach((linkedScenario: PlainProgramScenario) => {
      if (this.shouldCalculateAvailableUSF) {
        this.totalAvailableUSF += linkedScenario.totalAvailableUSF;
      }
      this.headcount += linkedScenario.headcount;
      this.headcountWithVacancy += linkedScenario.headcountWithVacancy;
      this.headcountWithUtilization += linkedScenario.headcountWithUtilization;
      this.workSeats += linkedScenario.workSeats;

      this.buildTotalProgramGrowths(head(linkedScenario.growths));
    });
    this.growthPercentage = this.calculateAverageGrowthPercentage() || 0;

    this.deskSharingRatio =
      this.workSeats === 0 ? 1 : this.headcountWithUtilization / this.workSeats;
    this.headcountUtilizationPercentage =
      this.headcountWithVacancy === 0
        ? 100
        : (this.headcountWithUtilization / this.headcountWithVacancy) * 100;
    this.vacancyFactorPercentage =
      this.headcount === 0
        ? 0
        : (this.headcountWithVacancy / this.headcount - 1) * 100;
    this.totalAvailableRSF =
      this.totalAvailableUSF * (1 + this.rsfLoadFactorPercentage / 100);
    this.workSeatsOriginal = this.workSeats;
  };

  buildTotalProgramGrowths = (lsGrowth: Growth) => {
    this.growths = [...this.growths, lsGrowth];
    const growth = this.growths.shift();

    this.growths = [
      {
        ...growth,
        headcountSeatCountBase:
          growth.headcountSeatCountBase + lsGrowth.headcountSeatCountBase,
        growthEntries: growth.growthEntries.map(
          (growthEntry: GrowthEntry, index) => {
            return {
              ...growthEntry,
              value:
                growthEntry.value + (lsGrowth.growthEntries[index]?.value || 0),
            };
          }
        ),
      },
      ...this.growths,
    ];
  };

  calculateAverageGrowthPercentage = () => {
    const growth: Growth = head(this.growths);
    if (growth && growth.growthEntries.length) {
      if (this.headcount) {
        const currentYearHeadcount = growth.headcountSeatCountBase;
        const exponent = 1 / (this.targetYear - this.startYear);
        const ratio = (this.headcount / currentYearHeadcount) ** exponent - 1;

        return ratio * 100;
      }
    }
    return 0;
  };

  static updateTotalProgramProps = (
    s: PlainProgramScenario,
    tps: ITotalProgram
  ): TPSScenario => ({
    ...s,
    id: tps.id,
    index: tps.index,
    name: tps.name,
    totalAvailableUSF: tps.usf,
  });

  toPlainObject = (): PlainProgramScenario => JSON.parse(JSON.stringify(this));

  static toAPITotalProgram = (
    s: TPSScenario
  ): ITotalProgram & { calculatorType: CalculatorType } => {
    return {
      id: s.id,
      name: s.name,
      index: s.index,
      subtotalProgramMultiplier: s.planningFactorPercentage,
      calculatorType: 'total-program',
      usf: s.totalAvailableUSF,
      usfType: (s as TotalProgramScenario).usfType,
      planningFactor: s.planningFactorPercentage,
      totalProgramScenario: s.linkedScenarios.map((linkedScenario) => ({
        id: linkedScenario.id,
        scenarioId: linkedScenario.id,
        totalProgramId: s.id,
      })),
      projectId: s.projectId,
      notes: s.notes,
    };
  };
}
